import { Modal } from 'antd-mobile'
import qs from 'query-string'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import * as actions from '../../../actions/orderActions'
import IconBack from '../../../assets/icons/arrow-back-black.svg'
import Cart from '../../../components/cart/Cart'
import DynamicColor from '../../../components/common/dynamic-color'
import Button from '../../../components/elements/Button'
import FormatCurrency from '../../../components/elements/FormatCurrency'
import RadioGroup from '../../../components/form/RadioGroup'
import {
    ACCESS_SERVICES_MAP,
    APP_SERVICE,
    COLOR_NAME,
    ORDER_METHOD,
    PARAMS_NAME,
    PAYMENT_METHOD,
} from '../../../constants'
import useDidMountEffect from '../../../hooks/useDidMountEffect'
import useOrder from '../../../hooks/useOrder'
import useOrderWaitPayment from '../../../hooks/useOrderWaitPayment'
import useTimeWork from '../../../hooks/useTimeWork'
import paths from '../../../routes/paths'
import { getRestaurantServices } from '../../../selectors'
import { canAddMoreGroup as canAddMoreGroupSelector } from '../../../selectors/order'
import { paymentMethodsSupport as paymentMethodsSupportSelector } from '../../../selectors/restaurant'
import {
    clsx,
    ensureObject,
    errorHandle,
    showMessageWarn,
} from '../../../utils/helper'
import {
    checkOverTime, makeAddressCalcDistance,
} from '../../../utils/index'
import ModalPayment from '../../ModalPayment'
import CustomerInfoContainer from './CustomerInfoContainer'
import TabletForm from './TabletForm'

const alertAnt = Modal.alert

let showPaymentMethod = sessionStorage.getItem('show-payment-type')

const CartContainer = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const { t } = useTranslation()

    const isReviewMode = APP_SERVICE === ORDER_METHOD.QR_CODE && history.location.state?.from === paths.tableQRcodeSuccess;
    const canAddMoreGroup = useSelector(canAddMoreGroupSelector)
    const services = useSelector(getRestaurantServices)
    const restaurantInfo = useSelector(state => state.RestaurantReducer)
    const tableNumber = useSelector(state => state.OrderReducer.tableNumber)
    const paymentMethodsSupport = useSelector(paymentMethodsSupportSelector)
    const {
        ordering: foodsInCart,
        orderMethod,
        deliverPosition,
        tabletForm
    } = useSelector(state => state.OrderReducer)
    const { isOvertime } = useTimeWork()
    const ordering = foodsInCart.filter(el => !el.submitted)
    const ordered = foodsInCart.filter(el => el.submitted)
    const { paypalClientId, paypalCurrentcyCode } = useSelector(
        state => state.RestaurantReducer
    )
    const {
        showPaymentPaypal,
        setShowPaymentPaypal,
        orderSuccessData,
        setOrderSuccessData,
    } = useOrderWaitPayment()
    const [showForm, setShowForm] = useState(orderMethod === ORDER_METHOD.QR_CODE)
    const [showTabletForm, setShowTabletForm] = useState(false)
    const [voucherVerified, setVoucherVerified] = useState(false)

    const { validShippingDistance, shippingDistanceMessage, shipMoney, total, originTotal } = useOrder();
    const userInfoRef = useRef()

    const handleSelectFood = food => {
        let foodData
        if (!food) {
            foodData = null
        } else {
            const { index, foodDetails, ...rest } = food
            foodData = {
                ...foodDetails,
                formData: rest,
                index,
            }
        }

        dispatch(actions.setViewFoodDetails(foodData))
    }

    const orderAtRestaurant = () => {
        dispatch(
            actions.submitOrder({
                params: {},
                onDone: ({ success, responseData }) => {
                    if (success && responseData.result) {
                        history.push(paths.tableQRcodeSuccess);
                    }
                },
            })
        )
    }

    const handleShowOrderStatus = data => {
        history.replace({
            pathname: history.location.pathname,
            search: qs.stringify({
                [APP_SERVICE === ACCESS_SERVICES_MAP.WEB
                    ? PARAMS_NAME.ORDER_WEB_ID
                    : PARAMS_NAME.ORDER_GO_ID]:
                    data?.tableNumber || orderSuccessData?.tableNumber,
            }),
        })
    }

    const handleStartPaymentPaypal = data => {
        history.replace({
            pathname: history.location.pathname,
            search: qs.stringify({
                [PARAMS_NAME.ORDER_WAIT_PAYMENT]:
                    data?.tableNumber || orderSuccessData?.tableNumber,
            }),
        })
        setShowPaymentPaypal(true)
    }

    const orderAtRestaurantAndTakeAGo = () => {
        dispatch(
            actions.submitOrder({
                params: {
                    isTakeAGo: true,
                },
                onDone: ({ success, responseData }) => {
                    if (success && responseData.result) {
                        const { data } = responseData || {}
                        setOrderSuccessData(data)

                        if (data?.paypalInfo) {
                            handleStartPaymentPaypal(data)
                        } else {
                            handleShowOrderStatus(data)
                        }
                    }
                },
            })
        )
    }

    const handleSubmitOrder = async () => {
        if (checkOverTime()) {
            return alertAnt('', t('modalOverTime.message'), [
                { text: t('button.agree') },
            ])
        }

        if (orderMethod === ORDER_METHOD.QR_CODE) {
            return orderAtRestaurant()
        }

        // valid customer form
        const customer = await userInfoRef.current.validateForm()
        if (!customer) {
            return
        }

        // valid shipping distance
        if (!validShippingDistance) {
            return showMessageWarn(shippingDistanceMessage);
        }

        // valid discount code
        const voucherState = userInfoRef.current?.voucherState
        if (voucherState?.code?.trim() && !voucherState?.verified) {
            return showMessageWarn(t('voucher.invalidVoucher'))
        }

        const voucherId = voucherState?.id

        // call save
        dispatch(
            actions.saveOrder({
                params: {
                    customer,
                    voucherId,
                },
                onDone: ({ success, responseData }) => {
                    if (!success || !responseData.result) {
                        return errorHandle({
                            message: 'apiMessage.saveOrderFailed',
                        })
                    }

                    const { data } = responseData || {}
                    setOrderSuccessData(data)

                    if (customer.paymentMethod === PAYMENT_METHOD.PAYPAL) {
                        handleStartPaymentPaypal(data)
                    } else {
                        handleShowOrderStatus(data)
                    }
                },
            })
        )
    }

    const handleChangeOrderMethod = e => {
        dispatch(actions.setOrderMethod(e.target.value))
    }

    const handleApprove = (data, actions) => {
        setShowPaymentPaypal(false)
        handleShowOrderStatus()
    }

    const handleCalcDistance = async desc => {
        if (orderMethod !== ORDER_METHOD.DELIVERY) {
            return
        }

        let destinations = desc
        if (!destinations) {
            const customer = await userInfoRef.current.getUserAddress()

            const { userAddressNumber, userStreet, userCity, userPostcode } =
                ensureObject(customer)
            destinations = makeAddressCalcDistance(userStreet, userAddressNumber, userCity, userPostcode);
        }

        dispatch(
            actions.calcDistance({
                params: {
                    destinations,
                },
                onError: () => {
                    userInfoRef.current.setErrors(prev => ({
                        ...prev,
                        userStreet: 'error',
                        userAddressNumber: 'error',
                        userCity: 'error',
                        userPostcode: 'error',
                    }));
                },
                onDone: () => {
                    userInfoRef.current.setErrors(prev => ({
                        ...prev,
                        userStreet: '',
                        userAddressNumber: '',
                        userCity: '',
                        userPostcode: '',
                    }));
                }
            })
        )
    }

    const checkValidAddress = () => {
        const { userAddressNumber, userStreet, userCity } =
            ensureObject(deliverPosition)

        return userAddressNumber && userStreet && userCity
    }

    const handleStartOrder = () => {
        if (checkOverTime()) {
            return alertAnt('', t('modalOverTime.message'), [
                { text: t('button.agree') },
            ])
        }

        if (orderMethod === ORDER_METHOD.TABLE_QRCODE) {
            // return orderAtRestaurantAndTakeAGo()
            if (showTabletForm || paymentMethodsSupport.length < 2) {
                orderAtRestaurantAndTakeAGo()
            } else {
                setShowTabletForm(true);
                setStorageShowPaymentMethodType();
            }
            return;
        }

        setShowForm(true)
    }

    const handleUpdateAmount = (amount, id, groupOrder, index) => {
        dispatch(actions.updateAmount({ amount, index, id, groupOrder, }));
    }

    const handleAddGroupOrder = () => dispatch(actions.addGroupOrder())

    const handlePayOther = payMethod => {
        dispatch(
            actions.payOther({
                params: {
                    orderID: orderSuccessData?.id,
                    payMethod,
                },
                onDone: ({ success, responseData }) => {
                    if (success) {
                        setShowPaymentPaypal(false)
                        handleShowOrderStatus()
                    }
                },
            })
        )
    }

    const handleGetOrdersQRCode = params =>
        dispatch(actions.getOrdersQRCode(params))

    useDidMountEffect(() => {
        if (orderMethod === ORDER_METHOD.DELIVERY && validAddress) {
            handleCalcDistance()
        }
    }, [orderMethod])

    useEffect(() => {
        orderMethod === ORDER_METHOD.QR_CODE &&
            handleGetOrdersQRCode({
                onError: () =>
                    history.push({
                        pathname: paths.notFound,
                        state: {
                            message: t('message.invalidTable'),
                        },
                    }),
            })

        const headerEle = document.getElementById('header-desk')
        const oldPosition = headerEle.style.position
        headerEle.style.position = 'fixed'

        return () => {
            headerEle.style.position = oldPosition
        }
    }, [])

    const setStorageShowPaymentMethodType = () => {
        console.log("setStorageShowPaymentMethodType");
        showPaymentMethod = true
        sessionStorage.setItem('show-payment-type', true)
        return true
    }

    const validAddress = checkValidAddress()

    const voucherState = userInfoRef.current?.voucherState
    const discountCodeEntered = voucherState?.code?.trim()
    const voucherNotVerified = discountCodeEntered && !voucherState?.verified

    const disableButtonOrder =
        isOvertime ||
        !ordering.length ||
        !validShippingDistance ||
        (orderMethod !== ORDER_METHOD.QR_CODE && !validAddress) ||
        voucherNotVerified

    const showFormInfo =
        showForm &&
        orderMethod !== ORDER_METHOD.QR_CODE &&
        !!ordering.length &&
        setStorageShowPaymentMethodType()

    const mapPmtMethodLabel = {
        [PAYMENT_METHOD.PAYPAL]: 'Paypal',
        [PAYMENT_METHOD.CASH]: t('cartMobile.lbCash'),
        [PAYMENT_METHOD.EC]: t('cartMobile.lbPayWithEC'),
        [PAYMENT_METHOD.CASH_CARD]: t('cartMobile.lbCashCard'),
    }

    const getDisplayPaymentMethodLabel = () => {
        if (showPaymentMethod &&
            !!ordering.length
        ) {
            const pmtLabel = mapPmtMethodLabel[(APP_SERVICE === ORDER_METHOD.TABLE_QRCODE ? tabletForm : deliverPosition)?.paymentMethod];

            if (!pmtLabel) {
                return null;
            }

            return (
                <div className="tag-pmt-method">
                    {pmtLabel}
                </div>
            );
        }

        return null;
    }

    const handleClickBack = e => {
        e.preventDefault();

        history.push( isReviewMode ? paths.tableQRcodeSuccess : paths.listFood);
    }

    return (
        <>
            <div className={clsx('cart-details', showFormInfo && 'show-form')}>
                <div className="cart-head-wrap">
                    <div className="container-fluid container-xl">
                        <div className="cart-head">
                            <a className="btn-back" onClick={handleClickBack} href="#">
                                <img
                                    width="30px"
                                    className="d-block"
                                    src={IconBack}
                                    alt="img back"
                                />
                            </a>

                            {tableNumber ? (
                                <DynamicColor
                                    style={{
                                        color: COLOR_NAME.BODY_TEXT,
                                        borderBottomColor: COLOR_NAME.BODY_LINE,
                                    }}
                                    className="cart-details__table-info"
                                >
                                    <span className="cart-details__table-info--title">
                                        {`${t(
                                            'cartMobile.lbTableNumber'
                                        )} ${tableNumber}`}
                                    </span>
                                </DynamicColor>
                            ) : (
                                t('cartMobile.title')
                            )}
                        </div>
                    </div>
                </div>
                <div
                    id="cart-food-list"
                    className="cart-details__main container-fluid container-xl"
                >
                    {showTabletForm && <TabletForm handleSubmit={handleStartOrder}/>}
                    {showFormInfo && (
                        <div className="customer-info-form">
                            <RadioGroup
                                list={[
                                    services.includes(
                                        ACCESS_SERVICES_MAP[
                                            ORDER_METHOD.DELIVERY
                                        ]
                                    ) && {
                                        label: t('cartMobile.lbDeliver'),
                                        value: ORDER_METHOD.DELIVERY,
                                    },
                                    services.includes(
                                        ACCESS_SERVICES_MAP[ORDER_METHOD.PICKUP]
                                    ) && {
                                        label: t('cartMobile.lbPickup'),
                                        value: ORDER_METHOD.PICKUP,
                                    },
                                ].filter(Boolean)}
                                label={t('cartMobile.lbOrderMethod')}
                                value={orderMethod}
                                onChange={handleChangeOrderMethod}
                                className="pt-4 receive-method-mobile"
                            />

                            <CustomerInfoContainer
                                handleCalcDistance={handleCalcDistance}
                                ref={userInfoRef}
                                onVoucherStateChange={setVoucherVerified}
                            />
                        </div>
                    )}
                    {!showFormInfo && !showTabletForm && (
                        <Cart
                            setFoodSelected={handleSelectFood}
                            restaurantInfo={restaurantInfo}
                            cartDetails={foodsInCart}
                            ordered={ordered}
                            originTotal={originTotal}
                            handleUpdateAmount={handleUpdateAmount}
                            handleAddGroupOrder={handleAddGroupOrder}
                            canAddMoreGroup={canAddMoreGroup && !isReviewMode}
                        />
                    )}
                </div>

                <div className="cart-footer-wrap">
                    <div className="cart-footer container-fluid container-xl">
                        {!!shipMoney && (
                            <div className="cart-hint ship-money mb-1">
                                {t('cartMobile.lbShippingPrice')}: 
                                <FormatCurrency
                                    className="ml-1"
                                    amount={shipMoney}
                                />
                            </div>
                        )}
                        <div className="cart-total">
                            <div className="total">
                                {t('cartMobile.lbTotalAmount')}:
                                <FormatCurrency
                                    className="ml-1"
                                    amount={total}
                                />
                            </div>

                            {getDisplayPaymentMethodLabel()}
                        </div>
                        {!validShippingDistance && (
                            <div className="cart-hint mt-1">{shippingDistanceMessage}</div>
                        )}
                        <div className="cart-actions">
                            {showForm ? (
                                !!ordering.length && (
                                    <Button
                                        disabled={disableButtonOrder}
                                        onClick={handleSubmitOrder}
                                        large
                                    >
                                        {t('cartMobile.btnSubmitOrder')}
                                    </Button>
                            )) : (
                                !!ordering.length ? (
                                    <>
                                        <Button
                                            large
                                            onClick={() => history.push(paths.listFood)}
                                            className="flex-1 mr-2"
                                        >
                                            {t('cartMobile.backToMenu')}
                                        </Button>
                                        <Button
                                            disabled={isOvertime}
                                            large
                                            onClick={handleStartOrder}
                                            className="flex-1"
                                        >
                                            {t(showTabletForm ? 'cartMobile.btnSubmitOrder' : 'cartMobile.btnStartOrder')}
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        large
                                        onClick={() => history.push(paths.listFood)}
                                    >
                                        {t('cartMobile.backToMenu')}
                                    </Button>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal payment method */}
            <ModalPayment
                showPaymentPaypal={showPaymentPaypal}
                orderSuccessData={orderSuccessData}
                paypalClientId={paypalClientId}
                handleApprove={handleApprove}
                paypalCurrentcyCode={paypalCurrentcyCode}
                handlePayOther={handlePayOther}
                orderMethod={orderMethod}
            />
        </>
    )
}

export default CartContainer
